import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BookingCoverageCandidatesResponse } from '@/types/api/queries';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';

import { CoverageCandidate } from './CoverageCandidate';

export const NO_COVERAGE = 'none';

const DEAD_ERRORS = [
  'card_inactive',
  'card_expired',
  'card_no_visits_remaining',
  'service_not_purchased',
  'service_expired',
  'coverage_already_held',
];

const isUsable = (candidate: { error: string | null }) =>
  !candidate.error || !DEAD_ERRORS.includes(candidate.error);

export const coverageCardValue = (id: number) => `card:${id}`;
export const coverageServiceValue = (id: number) => `service:${id}`;

type CoverageChoiceProps = {
  candidates?: BookingCoverageCandidatesResponse;
  isLoading: boolean;
  value: string;
  onChange: (value: string) => void;
};

export const CoverageChoice = (props: CoverageChoiceProps) => {
  const { candidates, isLoading, value, onChange } = props;
  const { t } = useTranslation();

  const cards = useMemo(
    () => (candidates?.client_cards ?? []).filter(isUsable),
    [candidates?.client_cards],
  );

  const services = useMemo(
    () => (candidates?.client_services ?? []).filter(isUsable),
    [candidates?.client_services],
  );

  const isEmpty = !cards.length && !services.length;

  return (
    <div className="grid gap-2">
      <Label>{t('What pays for it')}</Label>

      {isLoading && <Skeleton className="h-24 w-full" />}

      {!isLoading && (
        <RadioGroup
          value={value}
          onValueChange={onChange}
          className="grid gap-2"
        >
          <Label
            htmlFor={NO_COVERAGE}
            className="flex w-full cursor-pointer items-center gap-3 rounded-md border p-3 font-normal"
          >
            <RadioGroupItem
              value={NO_COVERAGE}
              id={NO_COVERAGE}
            />
            {t('Without a charge')}
          </Label>

          {cards.map((card) => (
            <CoverageCandidate
              key={coverageCardValue(card.id)}
              value={coverageCardValue(card.id)}
              candidate={card}
              details={[
                card.remaining_visits_count != null
                  ? `${t('Visits left')}: ${card.remaining_visits_count}`
                  : null,
                card.expires_on
                  ? `${t('Valid till')}: ${card.expires_on}`
                  : null,
              ].filter((detail): detail is string => !!detail)}
            />
          ))}

          {services.map((service) => (
            <CoverageCandidate
              key={coverageServiceValue(service.id)}
              value={coverageServiceValue(service.id)}
              candidate={service}
              details={[]}
            />
          ))}

          {isEmpty && (
            <p className="text-muted-foreground text-sm">
              {t('Nothing to pay with')}
            </p>
          )}
        </RadioGroup>
      )}
    </div>
  );
};
